import Skeleton from "@/v2-ui/skeleton"
import UptimeLatencyBadge from "@/v2-backoffice-client/uptime/UptimeLatencyBadge"
import UptimeLatencySparkline from "@/v2-backoffice-client/uptime/UptimeLatencySparkline"
import type { UptimeInlineTarget } from "@/v2-backoffice-client/uptime/inline/UptimeInlineStatus"

export type UptimeInlineLatencyProps = {
  target: UptimeInlineTarget | null | undefined
  loading?: boolean
}

/**
 * A target's response-time trend as one info-grid value: the last 24 hours
 * as a small sparkline (the same one the uptime table rows draw) next to the
 * 24h average as the shared colour-coded latency badge — amber when slow
 * against the target's own baseline, red when much slower — whose tooltip
 * names the class and the baseline. A target without a monitor, or without
 * samples, shows a dash; the Uptime item next to it says why.
 */
function UptimeInlineLatency(props: UptimeInlineLatencyProps) {
  const { target, loading } = props

  if (loading && !target) {
    return <Skeleton className="h-4 w-28" aria-busy="true" />
  }
  if (!target || target.AvgDurationMs24h == null) {
    return <span className="text-muted-foreground">—</span>
  }

  const baselineMs = target.P95DurationMs90d ?? target.AvgDurationMs90d ?? null

  return (
    <span className="flex items-center gap-2">
      <UptimeLatencySparkline
        className="uptime-inline-latency-sparkline"
        history={target.LatencyHourlyHistory}
        baselineP95Ms={target.P95DurationMs90d}
        isDown={target.LatestStatus === "down"}
      />
      <UptimeLatencyBadge
        currentMs={target.AvgDurationMs24h}
        baselineMs={baselineMs}
      />
    </span>
  )
}

export default UptimeInlineLatency
